import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RootLayout } from './components/RootLayout';
import { PublicLayout } from './components/PublicLayout';
import { RequireAdmin, RequireAdminRole } from './components/RequireAdmin';
import { TemplatePage } from './site/TemplatePage';
import { LoginPage } from './pages/LoginPage';
import { MemberAccountPage } from './pages/member/MemberAccountPage';
import { ResetPasswordPage } from './pages/member/ResetPasswordPage';
import { ConfirmEmailPage } from './pages/member/ConfirmEmailPage';

// The admin panel (and its heavier deps like recharts) is lazy-loaded so it
// stays out of the public site's initial bundle and only loads for admins.
const AdminLayout = lazy(() =>
  import('./admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminDashboardPage = lazy(() =>
  import('./admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
);
const AdminMembersPage = lazy(() =>
  import('./admin/AdminMembersPage').then((m) => ({ default: m.AdminMembersPage })),
);
const AdminAvailabilityPage = lazy(() =>
  import('./admin/AdminAvailabilityPage').then((m) => ({ default: m.AdminAvailabilityPage })),
);
const AdminEarningsPage = lazy(() =>
  import('./admin/AdminEarningsPage').then((m) => ({ default: m.AdminEarningsPage })),
);
const AdminTransactionsPage = lazy(() =>
  import('./admin/AdminTransactionsPage').then((m) => ({ default: m.AdminTransactionsPage })),
);
const AdminExceptionsPage = lazy(() =>
  import('./admin/AdminExceptionsPage').then((m) => ({ default: m.AdminExceptionsPage })),
);
const AdminAuditPage = lazy(() =>
  import('./admin/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })),
);
const AdminWebhooksPage = lazy(() =>
  import('./admin/AdminWebhooksPage').then((m) => ({ default: m.AdminWebhooksPage })),
);
const AdminHotelsPage = lazy(() =>
  import('./admin/AdminHotelsPage').then((m) => ({ default: m.AdminHotelsPage })),
);
const AdminSlidesPage = lazy(() =>
  import('./admin/AdminSlidesPage').then((m) => ({ default: m.AdminSlidesPage })),
);
const AdminFaqsPage = lazy(() =>
  import('./admin/AdminFaqsPage').then((m) => ({ default: m.AdminFaqsPage })),
);
const AdminUsersPage = lazy(() =>
  import('./admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);

// Public template pages. Every URL on the site is extension-less: /news, not
// /news.html, so these resolve at the clean path only.
const TEMPLATE_PAGES: { path: string; src: string }[] = [
  { path: 'about', src: 'about.html' },
  { path: 'gallery', src: 'gallery.html' },
  { path: 'news', src: 'news.html' },
  { path: 'events', src: 'events.html' },
  { path: 'faqs', src: 'faqs.html' },
  { path: 'contact', src: 'contacts.html' },
  { path: 'book', src: 'book.html' },
  { path: 'under-construction', src: 'under-construction.html' },
  { path: '503', src: '503.html' },
];

const templateRoutes = TEMPLATE_PAGES.map(({ path, src }) => ({
  path,
  element: <TemplatePage src={`/theme/pages/${src}`} />,
}));

// CloudFront 301s legacy .html URLs before they ever reach the app, but the dev
// server has no such function and old bookmarks still exist. Redirecting here
// too keeps a page reachable only at its canonical, extension-less URL.
const legacyRoutes = [
  { path: 'index.html', to: '/' },
  // The page was "Contacts" before it was named for what it holds.
  { path: 'contacts', to: '/contact' },
  { path: 'contacts.html', to: '/contact' },
  ...TEMPLATE_PAGES.map(({ path }) => ({ path: `${path}.html`, to: `/${path}` })),
].map(({ path, to }) => ({ path, element: <Navigate to={to} replace /> }));

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { index: true, element: <TemplatePage src="/theme/pages/home.html" /> },
      ...templateRoutes,
      ...legacyRoutes,
      // The public site's sign-in is the member one: these are the people the
      // site is for. The local (admin) login moves under /admin.
      { path: 'login', element: <TemplatePage src="/theme/pages/login.html" /> },
      { path: 'register', element: <TemplatePage src="/theme/pages/register.html" /> },
      { path: 'account', element: <MemberAccountPage /> },
      // One route for both halves of the reset: without ?token= it asks for the
      // email, with one it takes the new password. The member reaches the first
      // from sign-in and the second from the emailed link.
      { path: 'reset-password', element: <ResetPasswordPage /> },
      // Where the link in a member confirmation email lands. Ours since the
      // SecretsFL API stopped sending these; it posts the token to our API.
      { path: 'confirm-email', element: <ConfirmEmailPage /> },
      {
        element: <PublicLayout />,
        children: [{ path: 'admin/login', element: <LoginPage /> }],
      },
      {
        path: 'admin',
        element: (
          <RequireAdmin>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center text-slate-500">
                  Loading…
                </div>
              }
            >
              <AdminLayout />
            </Suspense>
          </RequireAdmin>
        ),
        children: [
          { index: true, element: <AdminDashboardPage /> },
          { path: 'members', element: <AdminMembersPage /> },
          {
            path: 'users',
            element: (
              <RequireAdminRole>
                <AdminUsersPage />
              </RequireAdminRole>
            ),
          },
          { path: 'availability', element: <AdminAvailabilityPage /> },
          { path: 'earnings', element: <AdminEarningsPage /> },
          { path: 'transactions', element: <AdminTransactionsPage /> },
          // A read of persisted verdicts, so staff may see it; the sweep that
          // fills it keeps its own admin gate in the API.
          { path: 'exceptions', element: <AdminExceptionsPage /> },
          { path: 'webhooks', element: <AdminWebhooksPage /> },
          {
            path: 'audit',
            element: (
              <RequireAdminRole>
                <AdminAuditPage />
              </RequireAdminRole>
            ),
          },
          { path: 'hotels', element: <AdminHotelsPage /> },
          { path: 'slides', element: <AdminSlidesPage /> },
          { path: 'faqs', element: <AdminFaqsPage /> },
        ],
      },
      { path: '*', element: <TemplatePage src="/theme/pages/404.html" /> },
    ],
  },
]);
